import { Alert, Checkbox, Modal } from "antd"
import { useContext, useState } from "react"
import { useDeleteInvoiceMutation, useInvoiceDetailsQuery } from "../../../domain/invoicing/invoicingQueries"
import { useInvoicingSettingsQuery } from "../../../domain/invoicing/settings/invoicingSettingsQueries"
import { useCustomerEmailsQuery } from "../../../domain/customers/customerQueries"
import { getBestCustomerEmail } from "../../../domain/customers/customer"
import { defaultInvoicingSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import { centsToDollarsDisplay } from "../../displayFormatters"
import VSpace from "../../common/VSpace"
import SendInvoiceModal from "../SendInvoice/SendInvoiceModal"
import PayInvoiceModal from "../PayInvoice/PayInvoiceModal"
import ExportInvoiceModal from "../InvoiceList/ExportInvoiceModal"
import ExportConfirmationModal from "../ExportConfirmationModal"
import InvoiceActionsContext from "./InvoiceActionsContext"
import InvoiceDetailModal from "../InvoiceList/InvoiceDetailModal"
import EmailCustomerInvoiceModal from "./EmailCustomerInvoiceModal"

const InvoiceActionModals = () => {
  const { activeModal, closeModal } = useContext(InvoiceActionsContext)
  const [exportConfirmationUrl, setExportConfirmationUrl] = useState<string | null>(null)
  const [deleteFromQuickBooks, setDeleteFromQuickBooks] = useState(true)

  const invoiceSummary = activeModal?.invoiceSummary
  const invoiceId = invoiceSummary?.invoiceId ?? ''
  const customerId = activeModal?.type === 'sendInvoice' ? (invoiceSummary?.customerId ?? 0) : 0

  const detailsQuery = useInvoiceDetailsQuery(activeModal?.type === 'exportToQb' ? invoiceId : '')
  const customerEmailsQuery = useCustomerEmailsQuery(customerId)
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const deleteMutation = useDeleteInvoiceMutation()

  const isExported = (invoiceSummary?.qboInvoiceId.length ?? 0) > 0
  const detachNotice = invoiceSummary?.statementId
    ? <Alert type="info" showIcon message="This invoice will be removed from its statement and handled on its own." />
    : null

  return <>
    <Modal
      open={activeModal?.type === 'delete'}
      title="Delete Invoice"
      okText="Delete"
      okType="primary"
      okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
      onOk={() => {
        if (!invoiceSummary) {
          return
        }
        deleteMutation.mutate({
          invoiceId: invoiceSummary.invoiceId,
          deleteFromQuickBooks,
          projectId: invoiceSummary.jobId
        })
        closeModal()
      }}
      onCancel={closeModal}
    >
      <VSpace>
        {detachNotice}
        <strong>
          Are you sure you want to delete this {centsToDollarsDisplay(invoiceSummary?.total ?? 0)} invoice?
        </strong>
        {isExported &&
          <Checkbox checked={deleteFromQuickBooks} onChange={(e) => setDeleteFromQuickBooks(e.target.checked)}>
            Also delete from QuickBooks
          </Checkbox>
        }
      </VSpace>
    </Modal>

    <SendInvoiceModal
      isOpen={activeModal?.type === 'sendInvoice'}
      onClose={closeModal}
      invoiceId={invoiceId}
      emailAddresses={[getBestCustomerEmail(customerEmailsQuery.data ?? []) ?? '']}
      invoicingSettings={invoicingSettingsQuery.data ?? defaultInvoicingSettings()}
      notice={detachNotice}
    />

    <PayInvoiceModal
      isOpen={activeModal?.type === 'payInvoice'}
      onClose={closeModal}
      invoiceId={invoiceId}
      customerId={invoiceSummary?.customerId ?? 0}
      notice={detachNotice}
    />

    {detailsQuery.data && <ExportInvoiceModal
      isOpen={activeModal?.type === 'exportToQb'}
      onClose={closeModal}
      onExported={(qboInvoiceUrl) => {
        closeModal()
        setExportConfirmationUrl(qboInvoiceUrl)
      }}
      invoice={detailsQuery.data.invoice}
    />}

    <ExportConfirmationModal
      isOpen={exportConfirmationUrl !== null}
      onClose={() => setExportConfirmationUrl(null)}
      qboInvoiceUrl={exportConfirmationUrl ?? ''}
    />

    <InvoiceDetailModal
      invoiceSummary={activeModal?.type === 'viewInvoice' ? invoiceSummary ?? null : null}
      onClose={closeModal}
    />

    <EmailCustomerInvoiceModal
      isOpen={activeModal?.type === 'emailReminder'}
      onClose={closeModal}
      invoiceSummary={activeModal?.type === 'emailReminder' ? invoiceSummary ?? null : null}
    />
  </>
}

export default InvoiceActionModals
